import {Trans} from '@lingui/macro';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import noResultsImageSrc from '../../../../assets/images/developers/genericEmptyState.svg';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {VirtualTableSortableColumnDirectionsEnum} from '../../../../components/common/table/helpers/tableHelpers';
import {useAsyncTableSource} from '../../../../components/common/table/helpers/useAsyncTableSource';
import {TableRowRenderOptions} from '../../../../components/common/table/tableRow';
import {VirtualTable, VirtualTableHandle} from '../../../../components/common/table/virtualTable';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {TableOffsetPaginationDataModel} from '../../../../models/table/tablePaginationDataModel';
import {buildSettingsDevelopersPath} from '../../../../models/url/settingsUrlHelpers';
import {
  SettingsDevelopersApplicationServersUrl,
  SettingsDevelopersPagesEnum,
  SettingsRootPagesEnum,
} from '../../../../models/url/settingsUrlModel';
import {fetchLocalApplicationServersAsyncAction} from '../../../../store/data/localApplications/localApplicationsActions';
import {
  developersServersTableColumns,
  developersServersTableDateCreatedColumnId,
} from './developersServersTableColumns';
import {DevelopersServersTableRowContainer} from './developersServersTableRowContainer';

/*
 * Constants.
 */

const sortableColumns = [
  {
    id: developersServersTableDateCreatedColumnId,
    directions: [VirtualTableSortableColumnDirectionsEnum.ASCENDING],
  },
];

/*
 * Props.
 */

interface DevelopersServersTableProps {
  url: SettingsDevelopersApplicationServersUrl;
  serverIds: ReadonlyArray<number>;
  paginationData?: TableOffsetPaginationDataModel;
}

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  top: 120px;
  left: 0;
  right: 0;
  text-align: center;
`;

const StyledEmptyStateTitleDiv = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
`;

const StyledEmptyStateDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  line-height: 21px;
  max-width: 259px;
  margin: auto;
  margin-bottom: 24px;
`;

const StyledHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: row-reverse;
`;

const StyledContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
  gap: 16px;
`;

const StyledEmptyStateButtonWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

/*
 * Component.
 */

export const DevelopersServersTable: FC<DevelopersServersTableProps> = (props) => {
  const {url, serverIds, paginationData} = props;

  const tableRef = useRef<VirtualTableHandle>();
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  const source = useAsyncTableSource(
    {
      fetcher: async () =>
        dispatch(
          fetchLocalApplicationServersAsyncAction({
            localApplicationId: url.applicationId,
            offset: paginationData?.offset ?? 0,
          }),
        ),
    },
    [],
  );

  const renderRow = (serverId: number, options: TableRowRenderOptions) => (
    <DevelopersServersTableRowContainer
      localApplicationId={url.applicationId}
      serverId={serverId}
      options={options}
      tableColumns={developersServersTableColumns}
      onSelectServer={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        navigateToLink(
          buildSettingsDevelopersPath({
            rootPage: SettingsRootPagesEnum.DEVELOPERS,
            subPage: SettingsDevelopersPagesEnum.APPLICATION_SERVERS,
            applicationId: url.applicationId,
            serverId,
          }),
        );
      }}
    />
  );

  const renderEmptyState = () => (
    <StyledEmptyStateContainerDiv>
      <img src={noResultsImageSrc} alt="no results" />
      <StyledEmptyStateTitleDiv>
        <Trans id="settings-developers-servers-table-empty-title">No servers</Trans>
      </StyledEmptyStateTitleDiv>
      <StyledEmptyStateDescriptionDiv>
        <Trans id="settings-developers-servers-table-empty-description">
          Create a server to define how your app should be authenticated.
        </Trans>
      </StyledEmptyStateDescriptionDiv>
      <StyledEmptyStateButtonWrapperDiv>
        {renderConnectServerButton(url, navigateToLink)}
      </StyledEmptyStateButtonWrapperDiv>
    </StyledEmptyStateContainerDiv>
  );

  return (
    <StyledContentWrapperDiv>
      <StyledHeaderWrapperDiv>{renderConnectServerButton(url, navigateToLink)}</StyledHeaderWrapperDiv>
      <StyledTableContainerDiv>
        <VirtualTable<number>
          ref={tableRef}
          columns={developersServersTableColumns}
          source={source}
          items={serverIds}
          paginationData={paginationData}
          renderRow={renderRow}
          renderEmpty={renderEmptyState}
          sortableColumns={sortableColumns}
          sortedColumn={{
            id: sortableColumns[0].id,
            direction: sortableColumns[0].directions[0],
          }}
        />
      </StyledTableContainerDiv>
    </StyledContentWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderConnectServerButton(
  url: SettingsDevelopersApplicationServersUrl,
  navigateToLink: HandlerOf<string>,
) {
  return (
    <Button
      variant={ButtonVariantsEnum.PRIMARY}
      onClick={() =>
        navigateToLink(
          buildSettingsDevelopersPath({
            rootPage: SettingsRootPagesEnum.DEVELOPERS,
            subPage: SettingsDevelopersPagesEnum.APPLICATION_SERVERS,
            applicationId: url.applicationId,
            isConnecting: true,
          }),
        )
      }
    >
      <Trans id="settings-developers-servers-table-connect-server">Connect server</Trans>
    </Button>
  );
}
